/**
 * Pillovely Delivery Widget
 *
 * Renders "furniture carry-in" delivery-service options on the product
 * form, blocks add-to-cart until one is chosen, and adds the chosen
 * service as a linked cart line (via a `_parent_key` property) alongside
 * the product.
 *
 * Shared verbatim by themeLive and themePreLive - keep the two copies in
 * sync, along with `snippets/pillovely-delivery-widget.liquid`.
 *
 * Both themes already own add-to-cart submission via
 * `class ProductForm extends HTMLFormElement` (assets/theme.js, registered
 * as `customElements.define('product-form', ProductForm, { extends: 'form' })`).
 * We don't want to reimplement that (bundles, section re-rendering,
 * quick-add modal handling, etc.), so instead this script layers on top:
 *
 * - A single document-level capturing `submit` listener gates any form
 *   that contains a `.pillovely-delivery-widget`: if no service is picked
 *   it blocks submission and shows a dialog; otherwise it resolves the
 *   delivery context (async - needs a `/cart.js` read) then re-submits via
 *   `form.requestSubmit()`, letting the theme's native `ProductForm`
 *   handler run unmodified for the actual add-to-cart.
 * - A document-level `ajaxProduct:added` listener (dispatched by
 *   `ProductForm.onSubmitHandler` on success) adds the matching delivery
 *   service as a second cart line once the parent line exists.
 * - Shopify's dynamic checkout button (`{{ form | payment_button }}`) is
 *   a cross-origin iframe that can't be intercepted directly, so a
 *   transparent overlay over `.shopify-payment-button` captures the click
 *   instead (see `interceptDynamicCheckout` below).
 *
 * @version 1.0.0
 */

(() => {
  'use strict';

  window.pillovelyDeliveryVariantCache = window.pillovelyDeliveryVariantCache || {};
  const pillovelyDeliveryVariantCache = window.pillovelyDeliveryVariantCache;
  window.pillovelyDeliveryProductCache = window.pillovelyDeliveryProductCache || {};
  const pillovelyDeliveryProductCache = window.pillovelyDeliveryProductCache;

  // =============================================================================
  // SHARED HELPERS (market/formatting/product lookups - theme-agnostic)
  // =============================================================================

  function pillovelyIsPolishMarket() {
    const shopify = window.Shopify || {};
    const locale = String(shopify.locale || '').toLowerCase();
    const market =
      typeof shopify.market === 'string'
        ? shopify.market
        : shopify.market && (shopify.market.handle || shopify.market.countryCode || shopify.market.country);

    return locale === 'pl' || String(market || '').toLowerCase() === 'pl';
  }

  function pillovelyFormatMoney(price) {
    const numericPrice = Number(price);
    if (Number.isNaN(numericPrice)) return '';

    const amount = typeof price === 'string' && price.includes('.')
      ? numericPrice
      : numericPrice / 100;

    return `${amount.toLocaleString('pl-PL', { maximumFractionDigits: 2 })} zł`;
  }

  async function pillovelyGetDeliveryProduct(handle) {
    if (!handle) return null;
    if (pillovelyDeliveryProductCache[handle]) return pillovelyDeliveryProductCache[handle];

    const response = await fetch(`/products/${handle}.json`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(`Nie znaleziono produktu uslugi: ${handle}`);
    }

    const payload = await response.json();
    const product = payload && payload.product;
    const variant = product && product.variants && product.variants[0];

    if (!product || !variant || !variant.id) {
      throw new Error(`Produkt uslugi nie ma wariantu: ${handle}`);
    }

    pillovelyDeliveryProductCache[handle] = product;
    pillovelyDeliveryVariantCache[handle] = variant.id;
    return product;
  }

  async function pillovelyGetDeliveryVariantId(handle) {
    if (!handle) return null;
    if (pillovelyDeliveryVariantCache[handle]) return pillovelyDeliveryVariantCache[handle];

    const product = await pillovelyGetDeliveryProduct(handle);
    return product && product.variants && product.variants[0] && product.variants[0].id;
  }

  // =============================================================================
  // WIDGET RENDERING
  // =============================================================================

  // Service products are titled "<usluga> - <kategoria>" (e.g. "Transport z
  // wniesieniem - meble tapicerowane salon"). The category half is redundant
  // in the picker - it always matches the product being viewed - so drop
  // everything from the first spaced dash on. Titles without one are kept
  // whole, and the spaces mean hyphenated words survive intact.
  function getDeliveryServiceLabel(title) {
    return String(title || '').split(/\s+[-–—]\s+/)[0].trim();
  }

  function getDeliveryServiceHandles(widget) {
    return (widget.dataset.deliveryServiceHandles || '')
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);
  }

  function appendDeliveryServiceOption(fieldset, option, index, formId) {
    const id = `DeliveryService-${formId}-${index}`;
    const label = document.createElement('label');
    label.className = 'pillovely-delivery-widget__option';
    label.setAttribute('for', id);

    const input = document.createElement('input');
    input.type = 'radio';
    input.name = 'pillovely_delivery_service';
    input.id = id;
    input.value = option.handle;
    input.dataset.serviceHandle = option.handle;
    input.checked = false;

    const text = document.createElement('span');
    text.className = 'pillovely-delivery-widget__option-text';

    const labelText = document.createElement('span');
    labelText.className = 'pillovely-delivery-widget__label';
    labelText.textContent = option.label;
    text.appendChild(labelText);

    if (option.price) {
      const price = document.createElement('span');
      price.className = 'pillovely-delivery-widget__price';
      price.textContent = option.price;
      text.appendChild(price);
    }

    label.append(input, text);
    fieldset.appendChild(label);
  }

  async function renderDeliveryServiceWidget(widget, formId) {
    if (!widget || !pillovelyIsPolishMarket()) return;

    const serviceHandles = getDeliveryServiceHandles(widget);
    if (!serviceHandles.length) return;

    if (widget.dataset.deliveryWarning) {
      console.warn(`[Pillovely delivery service] ${widget.dataset.deliveryWarning}`);
    }

    const fieldset = document.createElement('fieldset');
    fieldset.className = 'pillovely-delivery-widget__options';

    const legend = document.createElement('legend');
    legend.className = 'pillovely-delivery-widget__title subtitle-md';
    legend.textContent = 'Usługa dostawy';
    fieldset.appendChild(legend);

    appendDeliveryServiceOption(fieldset, {
      label: 'Przesyłka kurierska bez wniesienia',
      price: 'bezpłatnie',
      handle: '',
    }, 0, formId);

    widget.replaceChildren(fieldset);

    const serviceProducts = await Promise.all(
      serviceHandles.map(async (handle) => {
        try {
          return await pillovelyGetDeliveryProduct(handle);
        } catch (error) {
          console.warn(`[Pillovely delivery service] Nie mozna pobrac uslugi ${handle}.`, error);
          return null;
        }
      })
    );

    serviceProducts.filter(Boolean).forEach((product, index) => {
      const variant = product.variants && product.variants[0];
      appendDeliveryServiceOption(fieldset, {
        label: getDeliveryServiceLabel(product.title),
        price: variant && variant.price ? pillovelyFormatMoney(variant.price) : '',
        handle: product.handle,
      }, index + 1, formId);
    });
  }

  // =============================================================================
  // SELECTION / VALIDATION
  // =============================================================================

  function getSelectedDeliveryServiceHandle(widget) {
    const selected = widget.querySelector('input[name="pillovely_delivery_service"]:checked');
    return selected ? selected.dataset.serviceHandle : '';
  }

  function isDeliveryServiceSelected(widget) {
    if (!widget || !pillovelyIsPolishMarket()) return true;
    return Boolean(widget.querySelector('input[name="pillovely_delivery_service"]:checked'));
  }

  function showDeliveryRequiredDialog(form) {
    let dialog = form.querySelector('.pillovely-delivery-dialog');

    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'pillovely-delivery-dialog';

      const p = document.createElement('p');
      p.textContent = 'Wybierz usługę dostawy przed dodaniem do koszyka.';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'button button--primary pillovely-delivery-dialog__close';
      btn.setAttribute('is', 'hover-button');
      btn.innerHTML = '<span class="btn-fill" data-fill></span><span class="btn-text">OK</span>';
      btn.addEventListener('click', () => dialog.close());

      dialog.append(p, btn);
      form.appendChild(dialog);
    }

    dialog.showModal();
  }

  // =============================================================================
  // CART STATE / DELIVERY SERVICE RESOLUTION
  // =============================================================================

  async function fetchCartState() {
    try {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Nie udalo sie pobrac koszyka.');
      return await response.json();
    } catch (error) {
      console.warn('[Pillovely delivery service] Nie mozna sprawdzic aktualnego koszyka.', error);
      return null;
    }
  }

  function getExistingDeliveryService(cart, variantId) {
    if (!cart || !cart.items || !variantId) return null;

    const parentItem = cart.items.find((item) => {
      const isDeliveryServiceLine = item.properties && item.properties._parent_key;
      return !isDeliveryServiceLine && String(item.variant_id) === String(variantId);
    });

    if (!parentItem) return null;

    const childItem = cart.items.find((item) => {
      return item.properties && item.properties._parent_key === parentItem.key;
    });

    return { parentItem, childItem };
  }

  async function resolveDeliveryService(widget, formData) {
    const deliveryService = {
      handle: getSelectedDeliveryServiceHandle(widget),
      useParentQuantity: false,
    };

    if (!deliveryService.handle) return deliveryService;

    const cart = await fetchCartState();
    const existingDeliveryService = getExistingDeliveryService(cart, formData.get('id'));

    if (existingDeliveryService && !existingDeliveryService.childItem) {
      deliveryService.useParentQuantity = true;
    }

    return deliveryService;
  }

  async function addSelectedDeliveryService(parentResponse, formData, deliveryService) {
    const serviceHandle = deliveryService && deliveryService.handle;
    if (!serviceHandle || !parentResponse || !parentResponse.key) return null;

    try {
      const variantId = await pillovelyGetDeliveryVariantId(serviceHandle);
      if (!variantId) return null;

      const quantity = deliveryService.useParentQuantity
        ? Math.max(parseInt(parentResponse.quantity, 10) || 1, 1)
        : Math.max(parseInt(formData.get('quantity'), 10) || 1, 1);

      const payload = {
        id: variantId,
        quantity,
        properties: {
          _parent_key: parentResponse.key,
        },
      };

      const config = window.theme?.utils?.fetchConfig
        ? theme.utils.fetchConfig('javascript')
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify(payload);

      const addUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(addUrl, config);
      const parsedResponse = await response.json();

      if (!response.ok || parsedResponse.status) {
        console.warn(`[Pillovely delivery service] Nie udalo sie dodac uslugi ${serviceHandle}.`, parsedResponse);
        return null;
      }

      return parsedResponse;
    } catch (error) {
      console.warn(`[Pillovely delivery service] Pominieto usluge ${serviceHandle}.`, error);
      return null;
    }
  }

  async function refreshCartUi(source) {
    try {
      const cart = await (await fetch('/cart.js')).json();

      if (window.theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source, cart });
      }

      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: true } }));
    } catch (error) {
      console.warn('[Pillovely delivery service] Could not refresh cart UI.', error);
    }
  }

  // =============================================================================
  // SUBMIT GATE (blocks + re-submits through the native ProductForm handler)
  // =============================================================================

  // Single in-flight delivery context, consumed by the `ajaxProduct:added`
  // listener once the native handler's cart/add.js call succeeds.
  let pendingDeliveryContext = null;

  function hasWidget(form) {
    return form instanceof HTMLFormElement && Boolean(form.querySelector('.pillovely-delivery-widget'));
  }

  function handleDeliveryGate(event) {
    const form = event.target;

    if (!hasWidget(form)) return;

    if (form.dataset.pillovelyDeliveryResubmitting === 'true') {
      delete form.dataset.pillovelyDeliveryResubmitting;
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const submitter = event.submitter;

    (async () => {
      const widget = form.querySelector('.pillovely-delivery-widget');

      if (!isDeliveryServiceSelected(widget)) {
        showDeliveryRequiredDialog(form);
        return;
      }

      const formData = new FormData(form);
      const deliveryService = await resolveDeliveryService(widget, formData);
      pendingDeliveryContext = { form, formData, deliveryService };

      form.dataset.pillovelyDeliveryResubmitting = 'true';

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitter || undefined);
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    })();
  }

  async function handleAjaxProductAdded(event) {
    if (!pendingDeliveryContext) return;

    const { formData, deliveryService } = pendingDeliveryContext;
    pendingDeliveryContext = null;

    const parentResponse = event.detail && event.detail.product;
    const added = await addSelectedDeliveryService(parentResponse, formData, deliveryService);

    if (added) {
      await refreshCartUi('pillovely-delivery-widget');
    }
  }

  // =============================================================================
  // DYNAMIC CHECKOUT (Shop Pay / accelerated checkout button)
  // =============================================================================

  function interceptDynamicCheckout(form, widget) {
    const checkoutButton = form.querySelector('.shopify-payment-button');
    if (!checkoutButton || checkoutButton.dataset.pillovelyIntercepted === 'true') return;

    checkoutButton.dataset.pillovelyIntercepted = 'true';
    checkoutButton.style.position = 'relative';

    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:absolute;inset:0;z-index:1;cursor:pointer;';
    checkoutButton.appendChild(overlay);

    overlay.addEventListener('click', async (evt) => {
      evt.stopPropagation();
      evt.preventDefault();

      if (!isDeliveryServiceSelected(widget)) {
        showDeliveryRequiredDialog(form);
        return;
      }

      const submitButton = form.querySelector('[type="submit"]');
      submitButton?.setAttribute('aria-disabled', 'true');

      try {
        const formData = new FormData(form);
        const deliveryService = await resolveDeliveryService(widget, formData);

        const config = window.theme?.utils?.fetchConfig
          ? theme.utils.fetchConfig('javascript')
          : { method: 'POST', headers: { Accept: 'application/json' } };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const addUrl = window.theme?.routes?.cart_add_url || '/cart/add.js';
        const response = await fetch(addUrl, config);
        const result = await response.json();

        if (result.status) {
          return;
        }

        await addSelectedDeliveryService(result, formData, deliveryService);
        window.location.href = '/checkout';
      } catch (error) {
        console.error('[Pillovely delivery service] Buy now error:', error);
      } finally {
        submitButton?.removeAttribute('aria-disabled');
      }
    });
  }

  // =============================================================================
  // INITIALIZATION
  // =============================================================================

  let gateInstalled = false;

  function installGate() {
    if (gateInstalled) return;
    gateInstalled = true;

    document.addEventListener('submit', handleDeliveryGate, { capture: true });
    document.addEventListener('ajaxProduct:added', handleAjaxProductAdded);
  }

  function initDeliveryWidgets() {
    installGate();

    document.querySelectorAll('.pillovely-delivery-widget').forEach((widget) => {
      if (widget.dataset.pillovelyInitialized === 'true') return;
      widget.dataset.pillovelyInitialized = 'true';

      const form = widget.closest('form');
      if (!form) return;

      renderDeliveryServiceWidget(widget, form.id || form.getAttribute('id') || 'form');
      interceptDynamicCheckout(form, widget);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDeliveryWidgets);
  } else {
    initDeliveryWidgets();
  }

  document.addEventListener('shopify:section:load', initDeliveryWidgets);
})();
